import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { apiClient } from '../api/client';
import { formatAddress } from '../utils/formatters';

interface TraderRealizedGains {
  rank: number;
  address: string;
  totalProfit: number;
  roi: number;
}

interface TraderCombinedGains {
  rank: number;
  address: string;
  totalProfit: number;
  unrealizedProfit: number;
  combinedProfit: number;
  roi: number;
  unrealizedRoi: number;
}

interface FollowedWallet {
  rank: number;
  address: string;
  followerCount: number;
}

interface TraderLeaderboardData {
  topByRealizedGains: TraderRealizedGains[];
  topByCombinedGains: TraderCombinedGains[];
}

interface FollowedWalletsData {
  topFollowedWallets: FollowedWallet[];
}

// Helper function to format numbers
const formatNumber = (num: number): string => {
  if (isNaN(num)) return '0';

  if (Math.abs(num) >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (Math.abs(num) >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Helper function to format XRP amounts
const formatXRP = (num: number): string => {
  return `${formatNumber(num)} XRP`;
};

// Hook to fetch trader leaderboard data
const useTraderLeaderboard = () => {
  return useQuery({
    queryKey: ['traderLeaderboard'],
    queryFn: async (): Promise<TraderLeaderboardData> => {
      const response = await apiClient.get<any>('/analytics/traders?limit=10');
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error('Failed to fetch trader leaderboard');
    },
    staleTime: 60 * 1000, // 1 minute
    refetchOnWindowFocus: false,
  });
};

// Hook to fetch followed wallets leaderboard data
const useFollowedWallets = () => {
  return useQuery({
    queryKey: ['followedWalletsLeaderboard'],
    queryFn: async (): Promise<FollowedWalletsData> => {
      const response = await apiClient.get<any>('/analytics/followed-wallets?limit=10');
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error('Failed to fetch followed wallets leaderboard');
    },
    staleTime: 60 * 1000, // 1 minute
    refetchOnWindowFocus: false,
  });
};

export const LeaderboardPage: React.FC = () => {
  const { data, isLoading, isError } = useTraderLeaderboard();
  const { data: followedData, isLoading: followedLoading, isError: followedError } = useFollowedWallets();

  if (isLoading) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full items-center justify-center px-6 py-12">
          <div className="text-body font-body text-subtext-color">Loading leaderboard data...</div>
        </div>
      </DefaultPageLayout>
    );
  }

  if (isError) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full flex-col items-center justify-center px-6 py-12">
          <div className="text-heading-2 font-heading-2 text-error-600 mb-4">Error loading data</div>
          <div className="text-body font-body text-subtext-color">Please try refreshing the page</div>
        </div>
      </DefaultPageLayout>
    );
  }

  const { topByRealizedGains = [], topByCombinedGains = [] } = data || {};
  const { topFollowedWallets = [] } = followedData || {};

  return (
    <DefaultPageLayout>
      <div className="flex w-full flex-col items-start gap-6 mobile:gap-4">
        <span className="text-heading-1 font-heading-1 text-default-font mobile:text-heading-2 mobile:font-heading-2">
          Leaderboard
        </span>

        {/* Information note - moved to top */}
        <p className="text-body font-body text-default-font w-full">
          Realized gains are profits from NFTs that have been bought and sold. Unrealized gains are estimated profits from currently held NFTs based on collection floor prices. Minted and transferred NFTs are excluded from calculations. Click on a trader to view their full wallet profile.
        </p>

        {/* Statistics Cards */}
        <div className="grid w-full grid-cols-1 md:grid-cols-2 gap-4">
          {/* Top Realized Gains Card */}
          <div className="flex flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Top Realized Gains
              </span>
              <span className="text-caption font-caption text-subtext-color">
                Traders with highest profit from completed sales
              </span>
            </div>
            <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
            <div className="flex w-full flex-col items-start gap-3">
              {topByRealizedGains.length === 0 ? (
                <div className="text-body font-body text-subtext-color py-4">
                  No traders with realized gains yet
                </div>
              ) : (
                topByRealizedGains.map((trader, idx) => (
                  <div
                    key={trader.address}
                    className="flex w-full items-center gap-3"
                  >
                    <span className="text-body-bold font-body-bold text-subtext-color w-6 text-center">
                      {idx + 1}
                    </span>
                    <div className="flex grow shrink-0 basis-0 flex-col items-start">
                      <a
                        href={`/wallets/${trader.address}`}
                        className="line-clamp-1 w-full text-body-bold font-body-bold text-brand-700 hover:text-brand-800 hover:underline font-mono"
                        title={trader.address}
                      >
                        {formatAddress(trader.address)}
                      </a>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-body-bold font-body-bold text-success-600">
                        +{formatXRP(trader.totalProfit)}
                      </span>
                      {trader.roi !== 0 && (
                        <span className="text-caption font-caption text-success-500">
                          +{trader.roi.toFixed(1)}% ROI
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Top Combined Gains Card */}
          <div className="flex flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Top Total Gains
              </span>
              <span className="text-caption font-caption text-subtext-color">
                Traders with highest combined realized and unrealized profit
              </span>
            </div>
            <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
            <div className="flex w-full flex-col items-start gap-3">
              {topByCombinedGains.length === 0 ? (
                <div className="text-body font-body text-subtext-color py-4">
                  No traders with gains yet
                </div>
              ) : (
                topByCombinedGains.map((trader, idx) => (
                  <div
                    key={trader.address}
                    className="flex w-full items-center gap-3"
                  >
                    <span className="text-body-bold font-body-bold text-subtext-color w-6 text-center">
                      {idx + 1}
                    </span>
                    <div className="flex grow shrink-0 basis-0 flex-col items-start">
                      <a
                        href={`/wallets/${trader.address}`}
                        className="line-clamp-1 w-full text-body-bold font-body-bold text-brand-700 hover:text-brand-800 hover:underline font-mono"
                        title={trader.address}
                      >
                        {formatAddress(trader.address)}
                      </a>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-body-bold font-body-bold text-success-600">
                        +{formatXRP(trader.combinedProfit)}
                      </span>
                      <span className="text-caption font-caption text-subtext-color mobile:hidden">
                        Realized: +{formatXRP(trader.totalProfit)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Most Followed Wallets Card */}
        <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm mobile:px-4 mobile:py-4">
          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-heading-2 font-heading-2 text-default-font mobile:text-heading-3 mobile:font-heading-3">
              Most Followed Wallets
            </span>
            <span className="text-caption font-caption text-subtext-color">
              Wallets with the most followers
            </span>
          </div>
          <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
          <div className="flex w-full flex-col items-start gap-3 mobile:gap-2">
            {followedLoading ? (
              <div className="text-body font-body text-subtext-color py-4">
                Loading...
              </div>
            ) : followedError ? (
              <div className="text-body font-body text-error-600 py-4">
                Failed to load followed wallets
              </div>
            ) : topFollowedWallets.length === 0 ? (
              <div className="text-body font-body text-subtext-color py-4">
                No followed wallets yet
              </div>
            ) : (
              topFollowedWallets.map((wallet, idx) => (
                <div
                  key={wallet.address}
                  className="flex w-full items-center gap-3 mobile:gap-2"
                >
                  <span className="text-body-bold font-body-bold text-subtext-color w-6 text-center">
                    {idx + 1}
                  </span>
                  <div className="flex grow shrink-0 basis-0 flex-col items-start min-w-0">
                    <a
                      href={`/wallets/${wallet.address}`}
                      className="line-clamp-1 w-full text-body-bold font-body-bold text-brand-700 hover:text-brand-800 hover:underline font-mono truncate"
                      title={wallet.address}
                    >
                      {formatAddress(wallet.address)}
                    </a>
                  </div>
                  <div className="flex flex-col items-end flex-shrink-0">
                    <span className="text-body-bold font-body-bold text-default-font whitespace-nowrap">
                      <span className="mobile:hidden">{wallet.followerCount.toLocaleString()} {wallet.followerCount === 1 ? 'follower' : 'followers'}</span>
                      <span className="hidden mobile:inline">{wallet.followerCount.toLocaleString()}</span>
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </DefaultPageLayout>
  );
};
